'use client';

import Image from 'next/image';
import React from 'react';
import { FaCalendarAlt, FaClock, FaFacebookF, FaLinkedinIn, FaTwitter, FaUser, FaWhatsapp } from 'react-icons/fa';

import Breadcrumb from '@/components/Breadcrumb';
import Header from '@/components/Header';

import { useLanguage } from '@/contexts/LanguageContext';

export default function DudakDolgusuSonrasiOpusme() {
  const { t } = useLanguage();
  const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://drbariscin.com/tr/blog/dudak-dolgusu-sonrasi-opusme-bilimsel-estetik-ve-duygusal-bir-rehber';
  const pageTitle = 'Dudak Dolgusu Sonrası Öpüşme: Bilimsel, Estetik ve Duygusal Bir Rehber';
  const pageDescription = 'Dudak dolgusu sonrası öpüşmek ne zaman güvenlidir? Dolgunun kayması, enfeksiyon ve estetik riskler hakkında bilimsel açıklama ve uzman önerileri.';

  const handleShare = (platform: string) => {
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(pageTitle + ' - ' + pageUrl)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 to-pink-100">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Breadcrumb
            items={[
              { label: 'Ana Sayfa', href: '/tr' },
              { label: 'Blog', href: '/tr/blog' },
              { label: 'Dudak Dolgusu Sonrası Öpüşme', href: '/tr/blog/dudak-dolgusu-sonrasi-opusme-bilimsel-estetik-ve-duygusal-bir-rehber' },
            ]}
          />
        </div>

        {/* Article Header */}
        <article className="bg-white rounded-3xl shadow-xl overflow-hidden">
          {/* Featured Image */}
          <div className="relative h-64 sm:h-80">
            <Image
              src="/images/terleme-tedavisi.webp"
              alt="Dudak dolgusu sonrası öpüşme ve güvenlik rehberi"
              fill
              className="object-cover"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <span className="bg-rose-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                Dudak Estetiği
              </span>
            </div>
          </div>

          {/* Article Content */}
          <div className="p-6 sm:p-8">
            {/* Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Dudak Dolgusu Sonrası Öpüşme: Bilimsel, Estetik ve Duygusal Bir Rehber
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-6 mb-8 text-sm text-gray-600 border-b border-gray-200 pb-6">
              <div className="flex items-center">
                <FaUser className="w-4 h-4 mr-2" />
                <span>Op. Dr. Barış Çin</span>
              </div>
              <div className="flex items-center">
                <FaCalendarAlt className="w-4 h-4 mr-2" />
                <span>10 Mart 2026</span>
              </div>
              <div className="flex items-center">
                <FaClock className="w-4 h-4 mr-2" />
                <span>11 dakika okuma</span>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="flex items-center gap-4 mb-8">
              <span className="text-gray-600 font-medium">Paylaş:</span>
              <div className="flex gap-3">
                <button className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors" onClick={() => handleShare('facebook')}>
                  <FaFacebookF className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-400 text-white rounded-full hover:bg-blue-500 transition-colors" onClick={() => handleShare('twitter')}>
                  <FaTwitter className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors" onClick={() => handleShare('linkedin')}>
                  <FaLinkedinIn className="w-4 h-4" />
                </button>
                <button className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors" onClick={() => handleShare('whatsapp')}>
                  <FaWhatsapp className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Dudak dolgusu, yüzün en dikkat çeken bölgesine hacim ve denge kazandıran en popüler medikal estetik işlemlerden biridir.
                Ancak işlem sonrası bakım, en az uygulamanın kendisi kadar önemlidir.
                Hastaların en sık sorduğu ve en çok yanlış anlaşılan sorulardan biri şudur:
              </p>

              <div className="bg-rose-50 border-l-4 border-rose-400 p-6 mb-6">
                <p className="text-rose-800 font-medium text-lg">
                  "Dudak dolgusu yaptırdım, ne zaman öpüşebilirim?"
                </p>
              </div>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Bu soru hem fizyolojik hem de psikolojik yönüyle oldukça anlamlıdır.
                Çünkü dudak, sadece bir estetik bölge değil, duygusal bir iletişim organıdır.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1️⃣ Dudak Dolgusu Nasıl Etki Eder?
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Modern dudak dolgularında genellikle hyaluronik asit (HA) kullanılır.
                Bu madde cilt altında su tutarak hacim kazandırır.
                İşlem sonrası ilk 48 saat boyunca:
              </p>

              <div className="bg-blue-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside space-y-2 text-blue-800">
                  <li>Moleküller dokuya yerleşir,</li>
                  <li>Su bağlanma dengesi oluşur,</li>
                  <li>Mikroskobik damarlar yeniden düzenlenir.</li>
                </ul>
              </div>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Yani bu dönemde dudaklar fizyolojik olarak "yerleşme süreci" içindedir.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                2️⃣ Öpüşmek Neden Risklidir?
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Öpüşme sırasında dudak kasları aktif şekilde kasılır, basınç değişir ve sürtünme oluşur.
                Bu faktörler dolgu maddesinin henüz sabitlenmediği ilk günlerde ciddi etki yaratabilir.
              </p>

              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-rose-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Risk</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Açıklama</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Basınç etkisi</td>
                      <td className="border border-gray-300 px-4 py-3">Dolgu partikülleri yer değiştirebilir (asimetrik görünüm)</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Kas aktivitesi</td>
                      <td className="border border-gray-300 px-4 py-3">Dolgu dokuya tam yerleşmeden kas hareketi yön değiştirir</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Sürtünme / ısı artışı</td>
                      <td className="border border-gray-300 px-4 py-3">Lokal ödem ve şişlik artar</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Mikrotravma</td>
                      <td className="border border-gray-300 px-4 py-3">Enjeksiyon noktalarında tahriş riski oluşur</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-orange-50 border-l-4 border-orange-400 p-4 mb-6">
                <p className="text-orange-800 font-medium">
                  📍 Sonuç: İlk 48 saatte öpüşmek, dolgunun homojenliğini bozabilir.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                3️⃣ Enfeksiyon Riski
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Dudak, ağız florasına en yakın bölgedir.
                İşlemden hemen sonra ciltte mikroskobik iğne delikleri bulunur.
                Bu dönemde temas, bakterilerin içeri girmesine yol açabilir.
                Bu nedenle dolgu sonrası 48 saat boyunca öpüşme enfeksiyon riski taşır.
              </p>

              <p className="text-gray-700 mb-4 leading-relaxed">
                Belirtiler:
              </p>

              <div className="bg-red-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside space-y-2 text-red-800">
                  <li>Lokal kızarıklık</li>
                  <li>Ağrı</li>
                  <li>Şişlik</li>
                  <li>Isı artışı</li>
                </ul>
                <p className="text-red-800 mt-4">
                  Bu durum nadir de olsa antibiyotik tedavisi gerektirebilir.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                4️⃣ Zaman Çizelgesi
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-green-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Süre</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Durum</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Öneri</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">0–48 saat</td>
                      <td className="border border-gray-300 px-4 py-3">Dolgu yerleşiyor</td>
                      <td className="border border-gray-300 px-4 py-3">Kesinlikle öpüşmeyin</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">48–72 saat</td>
                      <td className="border border-gray-300 px-4 py-3">Hafif ödem var</td>
                      <td className="border border-gray-300 px-4 py-3">Hâlâ riskli dönem</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">3–5 gün</td>
                      <td className="border border-gray-300 px-4 py-3">Doku dengesi oturur</td>
                      <td className="border border-gray-300 px-4 py-3">Hafif temas serbest</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">7. günden sonra</td>
                      <td className="border border-gray-300 px-4 py-3">Dolgu sabitlenmiştir</td>
                      <td className="border border-gray-300 px-4 py-3">Normal öpüşme güvenlidir</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-indigo-50 border-l-4 border-indigo-400 p-4 mb-6">
                <p className="text-indigo-800 font-medium">
                  💬 Kısacası: sabırla beklemek, hem estetik hem duygusal sonucu korur.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                5️⃣ Klinik Örnek
              </h2>
              
              <div className="bg-blue-50 border-l-4 border-blue-400 p-6 mb-6">
                <p className="text-blue-800 italic">
                  "Bir hastam, dolgu işleminden bir gün sonra eşini öpmüş ve ertesi gün dudakta küçük bir şişlik oluşmuştu.
                  Dolgu yer değiştirmemişti ama ödem 3 gün sürdü.
                  Aynı işlemi bir ay sonra yaptığımızda bu kez 72 saat bekledi ve hiçbir problem yaşanmadı."
                </p>
                <p className="text-blue-800 font-medium mt-4">
                  — Dr. Barış Çin, Estetik Cerrahi Uzmanı
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                6️⃣ Estetik Psikoloji Perspektifi
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Dudak dolgusu yaptıran birçok hasta, hem fiziksel hem de duygusal olarak değişimi hisseder.
                Yeni bir dudak formu, kişinin kendine güvenini artırır.
                Ancak bu dönemde sabırsız davranmak, hem fiziksel sonucu hem psikolojik tatmini gölgeleyebilir.
              </p>

              <div className="bg-purple-50 border-l-4 border-purple-400 p-4 mb-6">
                <p className="text-purple-800 font-medium">
                  💬 "Sevgi bekler; estetik de."
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                7️⃣ Dudak Dolgusu Sonrası Kaçınılması Gerekenler
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-red-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Etken</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Neden</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Öpüşme</td>
                      <td className="border border-gray-300 px-4 py-3">Basınç ve enfeksiyon riski</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Sigara içmek</td>
                      <td className="border border-gray-300 px-4 py-3">Damar daralması ve mikrop riski</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Pipet kullanımı</td>
                      <td className="border border-gray-300 px-4 py-3">Dudak kaslarını aşırı çalıştırır</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Sıcak içecekler</td>
                      <td className="border border-gray-300 px-4 py-3">Damar genişlemesi ve ödem</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Ağız içi işlemler (diş hekimi vb.)</td>
                      <td className="border border-gray-300 px-4 py-3">Enfeksiyon riski</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                8️⃣ Dudak Dolgusu Sonrası Güvenli Temas Kuralları
              </h2>
              
              <div className="bg-green-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside space-y-2 text-green-800">
                  <li>48 saat boyunca dudaklara basınç uygulamayın.</li>
                  <li>3. günden sonra hafif dokunuşlar genelde güvenlidir.</li>
                  <li>7. günden itibaren dolgu dokuyla bütünleştiği için öpüşme doğal ve güvenli hale gelir.</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                9️⃣ Partner Sağlığı da Önemlidir
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Eğer partnerde dudak uçuğu (herpes simplex virüsü) varsa, işlem sonrası temas kesinlikle yasaktır.
                Çünkü virüs, dolgu yapılan dokuya bulaşırsa enfeksiyon riski katlanır.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                🔟 Sık Sorulan Sorular (FAQ)
              </h2>
              
              <div className="space-y-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">1. Dudak dolgusu sonrası öpüşürsem dolgu bozulur mu?</h3>
                  <p className="text-gray-600">Evet, erken dönemde basınç etkisiyle asimetri gelişebilir.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">2. Ne kadar beklemeliyim?</h3>
                  <p className="text-gray-600">En az 72 saat, ideal olarak 7 gün.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">3. Dudaklarım şiş, bu normal mi?</h3>
                  <p className="text-gray-600">Evet, işlemden sonraki 2–3 gün normaldir.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">4. Partnerimde uçuk var, ne zaman temas edebilirim?</h3>
                  <p className="text-gray-600">En az 10 gün sonra.</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">5. Hafif dokunuş yapabilir miyim?</h3>
                  <p className="text-gray-600">3. günden sonra hafif dokunuşlar genelde güvenlidir.</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">6. Enfeksiyon belirtileri nelerdir?</h3>
                  <p className="text-gray-600">Kızarıklık, ağrı, şişlik ve ısı artışı. Bu durumda hemen doktora başvurun.</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1️⃣1️⃣ Uzman Görüşü
              </h2>
              
              <div className="bg-green-50 border-l-4 border-green-400 p-6 mb-6">
                <p className="text-green-800 italic">
                  "Dudak dolgusundan sonra öpüşmek duygusal bir refleks olabilir, ama estetikte sabır çok değerlidir.
                  Dolgu yerleşmeden yapılan temas, sadece sonucu değil, sağlığı da riske atar."
                </p>
                <p className="text-green-800 font-medium mt-4">
                  — Dr. Barış Çin, Estetik Cerrahi Uzmanı
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1️⃣2️⃣ Etik ve Klinik Not
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Estetik uygulamalar sadece fiziksel değil, duygusal bir deneyimdir.
                Hekimin görevi, hastayı yalnızca güzelleştirmek değil, bilinçlendirmektir.
              </p>

              <div className="bg-purple-50 border-l-4 border-purple-400 p-4 mb-6">
                <p className="text-purple-800 font-medium">
                  💬 "Estetikte en güzel dokunuş, doğru zamanda yapılan dokunuştur."
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1️⃣3️⃣ Sonuç ve Klinik Öneri
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Dudak dolgusu sonrası öpüşme, sabırla ertelendiğinde hiçbir risk taşımaz.
                Ancak erken dönemde yapılan temas, dolgu kayması ve enfeksiyon gibi istenmeyen sonuçlara yol açabilir.
              </p>

              <div className="bg-red-50 border-l-4 border-red-400 p-6 mb-6">
                <p className="text-red-800 font-medium">
                  🩺 Dr. Barış Çin öneriyor:
                </p>
                <p className="text-red-800 mt-2">
                  "48 saat beklemek estetik bir kural değil, bilimsel bir zorunluluktur.
                  Gerçek güzellik, doğru zamanda dokunmaktır."
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Kaynakça
              </h2>
              
              <div className="bg-gray-50 rounded-xl p-6 mb-8">
                <ul className="list-disc list-inside space-y-2 text-gray-700">
                  <li>ISAPS (2024) – "Post-Lip Filler Care Guidelines"</li>
                  <li>Teoxane Clinical Studies (2023) – "HA Integration Time and Pressure Sensitivity"</li>
                  <li>Harvard Health (2024) – "Infection Risk After Lip Injections"</li>
                  <li>TETD (2024) – "Dudak Dolgusu Sonrası Bakım Rehberi"</li>
                </ul>
              </div>

              <div className="flex flex-wrap gap-2 mb-8">
                <span className="bg-rose-100 text-rose-800 px-3 py-1 rounded-full text-sm">#dudakdolgu</span>
                <span className="bg-rose-100 text-rose-800 px-3 py-1 rounded-full text-sm">#öpüşme</span>
                <span className="bg-rose-100 text-rose-800 px-3 py-1 rounded-full text-sm">#estetik</span>
                <span className="bg-rose-100 text-rose-800 px-3 py-1 rounded-full text-sm">#DrBarışÇin</span>
                <span className="bg-rose-100 text-rose-800 px-3 py-1 rounded-full text-sm">#hyaluronikasit</span>
                <span className="bg-rose-100 text-rose-800 px-3 py-1 rounded-full text-sm">#enfeksiyon</span>
              </div>

              {/* Social Share Buttons */}
              <div className="border-t pt-8 mt-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Bu yazıyı paylaş:</h3>
                <div className="flex space-x-4">
                  <button
                    onClick={() => handleShare('facebook')}
                    className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <FaFacebookF className="w-4 h-4" />
                    <span>Facebook</span>
                  </button>
                  <button
                    onClick={() => handleShare('twitter')}
                    className="flex items-center space-x-2 bg-blue-400 text-white px-4 py-2 rounded-lg hover:bg-blue-500 transition-colors"
                  >
                    <FaTwitter className="w-4 h-4" />
                    <span>Twitter</span>
                  </button>
                  <button
                    onClick={() => handleShare('linkedin')}
                    className="flex items-center space-x-2 bg-blue-700 text-white px-4 py-2 rounded-lg hover:bg-blue-800 transition-colors"
                  >
                    <FaLinkedinIn className="w-4 h-4" />
                    <span>LinkedIn</span>
                  </button>
                  <button
                    onClick={() => handleShare('whatsapp')}
                    className="flex items-center space-x-2 bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors"
                  >
                    <FaWhatsapp className="w-4 h-4" />
                    <span>WhatsApp</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </article>

        {/* CTA Section */}
        <div className="mt-12 bg-gradient-to-r from-rose-600 to-pink-700 rounded-3xl p-8 text-white text-center">
          <h3 className="text-2xl font-bold mb-4">Dudak Dolgusu Uygulaması ve Bakım Danışmanlığı</h3>
          <p className="mb-6">Uzman doktorumuzdan dudak dolgusu uygulaması ve sonrası bakım hakkında detaylı bilgi alın.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={`https://wa.me/905438999586?text=${encodeURIComponent('Dudak dolgusu uygulaması ve sonrası bakım hakkında bilgi almak istiyorum.')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-6 py-3 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
            >
              <FaWhatsapp className="w-5 h-5 mr-2" />
              WhatsApp ile İletişim
            </a>
            <a
              href="/tr/iletisim"
              className="inline-flex items-center justify-center px-6 py-3 bg-white text-rose-600 rounded-full hover:bg-gray-100 transition-colors"
            >
              Randevu Al
            </a>
          </div>
        </div>

        {/* Related Articles */}
        <div className="mt-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">İlgili Yazılar</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <a
              href="/tr/blog/dudak-dolgusu-teknikleri-dogal-simetrik-ve-estetik-sonuclar"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Dudak Dolgusu Teknikleri</h4>
              <p className="text-gray-600 text-sm">Doğal, simetrik ve estetik sonuçlar için dudak dolgusu uygulama teknikleri.</p>
            </a>
            <a
              href="/tr/blog/en-iyi-dudak-dolgusu-markasi-dogallik-kalicilik-ve-guven"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">En İyi Dudak Dolgusu Markası</h4>
              <p className="text-gray-600 text-sm">Doğallık, kalıcılık ve güven açısından en iyi dudak dolgusu markaları.</p>
            </a>
            <a
              href="/tr/blog/dudak-dolgusunun-alerji-riski-gercek-olgular-yanlis-algilar-ve-guvenli-uygulama"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Dudak Dolgusunun Alerji Riski</h4>
              <p className="text-gray-600 text-sm">Dudak dolgu alerji yapar mı? Hyaluronik asit reaksiyonları ve güvenli uygulama kriterleri.</p>
            </a>
            <div className="bg-white rounded-2xl shadow-lg p-6 text-center">
              <p className="text-gray-600 mb-4">
                Daha fazla bilgilendirici içerik için bizi takip edin.
              </p>
              <a
                href="/tr/blog"
                className="inline-flex items-center px-4 py-2 bg-rose-600 text-white rounded-full hover:bg-rose-700 transition-colors text-sm"
              >
                Tüm Blog Yazılarını Görüntüle
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
